import { useMemo, useState } from 'react';
import {
  Box,
  Chip,
  FormControlLabel,
  Grid,
  Link,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import type {
  DashboardJobEnd,
  DashboardSeniorFeedback,
  DashboardQualtricsForm,
} from '@/api/dashboardApi';
import { Section, SectionCard, SectionHeader, StatusChip } from '@/components/common';
import { formatDateShort } from '@/utils/dateFormatter';
import { groupByProject, isDone, type ProjectGroup } from './ToDoSection.utils';

type SortDirection = 'asc' | 'desc';

interface ProjectCardProps {
  group: ProjectGroup;
  suppressQualtricsLinks?: boolean;
}

function ProjectCard({ group, suppressQualtricsLinks }: ProjectCardProps) {
  const [showDone, setShowDone] = useState(false);
  const [sortDir, setSortDir] = useState<SortDirection>('asc');

  const filteredItems = useMemo(() => {
    const items = showDone
      ? group.items
      : group.items.filter((item) => !isDone(item.status));
    return [...items].sort((a, b) => {
      const aDone = isDone(a.status);
      const bDone = isDone(b.status);
      if (aDone !== bDone) return aDone ? 1 : -1;
      const dateA = new Date(a.dueDate).getTime();
      const dateB = new Date(b.dueDate).getTime();
      return sortDir === 'asc' ? dateA - dateB : dateB - dateA;
    });
  }, [group.items, showDone, sortDir]);

  const nextDue = useMemo(() => {
    const pending = group.items.filter((item) => !isDone(item.status));
    if (pending.length === 0) return null;
    const sorted = [...pending].sort(
      (a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime()
    );
    return sorted[0]?.dueDate;
  }, [group.items]);

  return (
    <SectionCard>
      <Box
        component="header"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          flexWrap: 'wrap',
          gap: 1,
          marginBottom: 2,
        }}
      >
        <Typography component="h3" sx={{ typography: { xs: 'h6', md: 'h5' }, mr: 'auto' }}>
          {group.projectName}
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={showDone}
              onChange={(e) => setShowDone(e.target.checked)}
            />
          }
          label="Show done"
        />
        {nextDue && <Chip label={`Next due: ${formatDateShort(nextDue)}`} />}
      </Box>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>TO DO</TableCell>
              <TableCell sx={{ width: '25%', minWidth: 150 }}>
                <TableSortLabel
                  active
                  direction={sortDir}
                  onClick={() =>
                    setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
                  }
                >
                  DUE DATE
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ width: '15%', minWidth: 90 }}>STATUS</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredItems.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} sx={{ textAlign: 'center', py: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    {showDone ? 'No items.' : 'All done!'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredItems.map((item, index) => {
                const isFirstDone = isDone(item.status) && (index === 0 || !isDone(filteredItems[index - 1].status));
                const isLastToDo = !isDone(item.status) && (index === filteredItems.length - 1 || isDone(filteredItems[index + 1].status));
                return (
                <TableRow
                  key={item.id}
                  sx={{
                    '& td': {
                      borderBottom: 'none',
                      ...(isLastToDo && { pb: 2 }),
                      ...(isFirstDone && { borderTop: 1, borderColor: 'divider', pt: 2 }),
                    },
                  }}
                >
                  <TableCell>
                    {item.link && item.status && !(suppressQualtricsLinks && item.type === 'qualtricsForm') ? (
                      <Link
                        href={item.link}
                        underline="hover"
                        {...(item.type === 'qualtricsForm' && {
                          target: '_blank',
                          rel: 'noopener noreferrer',
                        })}
                      >
                        {item.title}
                      </Link>
                    ) : (
                       <> {item.title}</>
                    )}
                  </TableCell>
                  <TableCell>
                    {!isDone(item.status) && <Chip label={`Due: ${formatDateShort(item.dueDate)}`} />}
                  </TableCell>
                  <TableCell>
                    <StatusChip status={item.status} />
                  </TableCell>
                </TableRow>
              );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </SectionCard>
  );
}

export interface ToDoSectionProps {
  jobEnds: DashboardJobEnd[];
  seniorFeedback: DashboardSeniorFeedback[];
  qualtricsForm: DashboardQualtricsForm[];
  suppressQualtricsLinks?: boolean;
}

export function ToDoSection({
  jobEnds,
  seniorFeedback,
  qualtricsForm,
  suppressQualtricsLinks,
}: ToDoSectionProps) {
  const projectGroups = useMemo(() => {
    return groupByProject(jobEnds, seniorFeedback, qualtricsForm);
  }, [jobEnds, seniorFeedback, qualtricsForm]);

  return (
    <Section>
      <SectionHeader>TO DO - WEEKLY & PROJECT FEEDBACK</SectionHeader>
      {projectGroups.length === 0 ? (
        <Typography variant="body1" color="text.secondary">
          No items to display.
        </Typography>
      ) : (
        <Grid container spacing={{ xs: 1 }} sx={{ alignItems: 'stretch' }}>
          {projectGroups.map((group) => (
            <Grid
              size={{ xs: 12, md: projectGroups.length === 1 ? 12 : 6 }}
              key={group.projectName}
              sx={{ minWidth: 0, display: 'flex' }}
            >
              <ProjectCard group={group} suppressQualtricsLinks={suppressQualtricsLinks} />
            </Grid>
          ))}
        </Grid>
      )}
    </Section>
  );
}

export default ToDoSection;
